import {
  createContext,
  useContext,
  useState,
  useCallback,
  createElement,
} from 'react'
import type { ReactNode } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { cn } from '@/lib/cn'

// ── Types ────────────────────────────────────────────────────────────────────

type ToastVariant = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
}

interface ToastContextValue {
  toast: (opts: {
    title: string
    description?: string
    variant?: ToastVariant
    duration?: number
  }) => void
}

// ── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

// ── Styles ───────────────────────────────────────────────────────────────────

const variantStyles: Record<ToastVariant, string> = {
  success: 'border-green-200 bg-green-50 text-green-900',
  error:   'border-red-200   bg-red-50   text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  info:    'border-blue-200  bg-blue-50  text-blue-900',
}

const iconMap: Record<ToastVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error:   XCircle,
  warning: AlertTriangle,
  info:    Info,
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const toast = useCallback(
    ({
      title,
      description,
      variant = 'info',
      duration = 4000,
    }: {
      title: string
      description?: string
      variant?: ToastVariant
      duration?: number
    }) => {
      const id = `${Date.now()}-${Math.random()}`
      setToasts((prev) => [...prev, { id, title, description, variant, duration }])
    },
    [],
  )

  function dismiss(id: string) {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  return createElement(
    ToastContext.Provider,
    { value: { toast } },
    createElement(
      ToastPrimitive.Provider,
      { swipeDirection: 'right' },
      children,
      toasts.map((t) =>
        createElement(
          ToastPrimitive.Root,
          {
            key: t.id,
            duration: t.duration,
            onOpenChange: (open: boolean) => { if (!open) dismiss(t.id) },
            className: cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-md',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full',
              'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-full',
              'transition-all duration-200',
              variantStyles[t.variant],
            ),
          },
          createElement(iconMap[t.variant], {
            className: 'mt-0.5 h-4 w-4 shrink-0',
            'aria-hidden': true,
          }),
          createElement(
            'div',
            { className: 'flex-1 space-y-0.5' },
            createElement(
              ToastPrimitive.Title,
              { className: 'text-sm font-semibold leading-snug' },
              t.title,
            ),
            t.description
              ? createElement(
                  ToastPrimitive.Description,
                  { className: 'text-xs opacity-80' },
                  t.description,
                )
              : null,
          ),
          createElement(
            ToastPrimitive.Close,
            {
              'aria-label': 'Dismiss',
              className:
                'ml-auto shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current',
            },
            createElement(X, { className: 'h-3.5 w-3.5', 'aria-hidden': true }),
          ),
        ),
      ),
      createElement(ToastPrimitive.Viewport, {
        className:
          'fixed bottom-4 right-4 z-[100] flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2',
      }),
    ),
  )
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
